import { noop } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ActionType } from 'react-table';

import { Table } from '~/eds/src/modules/Table/Table';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useTableSettings } from '~/hooks/useTableSettings';
import { ColumnSortOrder } from '~/types/Columns';
import { ColumnWidths } from '~/types/Table';
import { DEFAULT_TABLE_SETTINGS } from '~/utils/table';

interface SetColumnOrder {
  (updatedColumnOrder: string[], actionMeta?: any): unknown;
}
interface TableUpdate {
  (state: TableState, action?: ActionType): unknown;
}
interface SetPageSize {
  (pageSize: number): unknown;
}

type OnPaginate = (page: { pageIndex: number }) => unknown;

type TableState = any;
type Column = { key: string; rawWidth?: number };

interface Props {
  columns?: Column[];
  context: string;
  onPageSizeChange?: SetPageSize;
  onSetColumnOrder?: SetColumnOrder;
  onUpdate?: TableUpdate;
  state?: TableState;
  onPaginate?: OnPaginate;
  [x: string]: any;
}

export const PersistedTable = ({
  columns = [],
  context,
  onPageSizeChange = noop,
  onSetColumnOrder = noop,
  onUpdate = noop,
  state = {},
  ...rest
}: Props) => {
  const defaultColumnOrder = columns.map((column) => column.key);

  const [columnOrder, setColumnOrder] = useState(
    state.columnOrder ?? defaultColumnOrder,
  );
  const [columnWidths, setColumnWidths] = useState<ColumnWidths>(
    state.columnWidths ?? DEFAULT_TABLE_SETTINGS.columnWidths,
  );
  const [pageSize, setPageSize] = useState<number>(
    state.pageSize ?? DEFAULT_TABLE_SETTINGS.pageSize,
  );
  const [sortBy, setSortBy] = useState<ColumnSortOrder>(state.sortBy?.[0]);

  const localTableSettings = {
    columnOrder,
    columnWidths,
    pageSize,
    sortBy,
  };

  const { tableSettings: _tableSettings, updateTableSettings } =
    useTableSettings(context);
  const tableSettings = _tableSettings ?? localTableSettings;
  const enableDebounce = useFlag(FlagType.PersistedTableDebounce);

  // Ref to prevent circular updates - track if we're updating from our own save (only needed when flag is enabled)
  const isUpdatingFromSaveRef = useRef(false);

  useEffect(() => {
    if (tableSettings) {
      if (enableDebounce) {
        // New behavior: Only sync if it's not from our own update (prevent circular updates)
        if (!isUpdatingFromSaveRef.current) {
          setColumnWidths(tableSettings.columnWidths);
          setPageSize(tableSettings.pageSize);
          setSortBy(tableSettings.sortBy);
        }
        // Reset the flag after sync
        isUpdatingFromSaveRef.current = false;
      } else {
        // Old behavior: Always sync from tableSettings
        setColumnWidths(tableSettings.columnWidths);
        setPageSize(tableSettings.pageSize);
        setSortBy(tableSettings.sortBy);
      }
    }
  }, [tableSettings, enableDebounce]);

  useEffect(() => {
    if (tableSettings.columnOrder) {
      setColumnOrder(tableSettings.columnOrder);
    }
  }, [tableSettings.columnOrder]);

  useEffect(() => {
    if (state?.columnOrder) {
      setColumnOrder(state.columnOrder);
    }
  }, [state?.columnOrder]);

  const handleColumnOrderChange: SetColumnOrder = (
    updatedColumnOrder,
    actionMeta,
  ) => {
    setColumnOrder(updatedColumnOrder);

    const newSettings = {
      ...tableSettings,
      columnOrder: updatedColumnOrder,
    };

    updateTableSettings(newSettings);

    onSetColumnOrder(updatedColumnOrder, actionMeta);
  };

  const handlePageSizeChange: SetPageSize = (pageSize: number) => {
    setPageSize(pageSize);

    const newSettings = {
      ...tableSettings,
      pageSize,
    };

    updateTableSettings(newSettings);

    onPageSizeChange(pageSize);
  };

  const handleUpdate: TableUpdate = (state, action) => {
    // Defer parent state updates to avoid "Cannot update component while rendering another"
    // (Table's state reducer runs during render). State stays in sync because Table already
    // has the new state; we only defer applying it in the parent.
    queueMicrotask(() => {
      if (action?.type === 'columnDoneResizing') {
        const columnWidths = {
          ...tableSettings.columnWidths,
          ...state.columnResizing.columnWidths,
        };

        if (enableDebounce) {
          // New behavior: Check if widths actually changed and prevent circular updates
          const widthsChanged =
            JSON.stringify(columnWidths) !==
            JSON.stringify(tableSettings.columnWidths);

          if (widthsChanged) {
            setColumnWidths(columnWidths);

            // Mark that we're updating from our own save to prevent circular update
            isUpdatingFromSaveRef.current = true;

            const newSettings = {
              ...tableSettings,
              columnWidths,
            };

            updateTableSettings(newSettings);
          }
        } else {
          // Old behavior: Immediate update and save
          setColumnWidths(columnWidths);

          const newSettings = {
            ...tableSettings,
            columnWidths,
          };

          updateTableSettings(newSettings);
        }
      }

      if (action?.type === 'toggleSortBy') {
        const sortBy = state.sortBy[0] as ColumnSortOrder;

        setSortBy(sortBy);

        const newSettings = {
          ...tableSettings,
          sortBy,
        };

        updateTableSettings(newSettings);
      }

      onUpdate(state, action);
    });
  };

  const columnsWithWidths = useMemo(() => {
    return columns.map((column) => ({
      ...column,
      rawWidth: columnWidths[column.key],
    }));
  }, [columns, columnWidths]);

  return (
    <Table
      {...rest}
      columns={columnsWithWidths}
      onPageSizeChange={handlePageSizeChange}
      onSetColumnOrder={handleColumnOrderChange}
      onUpdate={handleUpdate}
      state={{
        ...state,
        columnOrder,
        pageSize,
        sortBy: sortBy ? [sortBy] : [],
      }}
    />
  );
};
